import { type RefObject, useLayoutEffect } from 'react';

/** Keep restored dialog focus visible only after actual keyboard navigation. */
export function useFamilyFocusMode(
  rootRef: RefObject<HTMLElement>,
  returnFocusRef?: RefObject<HTMLElement>,
) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const source = returnFocusRef?.current ?? document.activeElement;
    root.dataset.familyFocus = source?.closest('[data-family-focus]')?.getAttribute('data-family-focus') ?? 'keyboard';

    // Track the dialog too: typing in a text field can otherwise make browsers
    // restore :focus-visible to a button originally activated with the pointer.
    const pointer = () => { root.dataset.familyFocus = 'pointer'; };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Tab' || event.key.startsWith('Arrow')) root.dataset.familyFocus = 'keyboard';
    };
    document.addEventListener('pointerdown', pointer, true);
    document.addEventListener('keydown', keyboard, true);
    return () => {
      document.removeEventListener('pointerdown', pointer, true);
      document.removeEventListener('keydown', keyboard, true);
    };
  }, [rootRef, returnFocusRef]);
}
